import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Briefcase, Plus, Trash2, Edit3 } from "lucide-react";
import HelpTip from "@/components/HelpTip";

const empty = {
  ragione_sociale: "", partita_iva: "", codice_fiscale: "",
  sdi_code: "", pec: "", email: "", phone: "",
  address: "", city: "", cap: "", country: "IT",
};

export default function Companies() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/companies");
      setRows(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = Object.fromEntries(
        Object.entries(form).map(([k, v]) => [k, v === "" ? null : v])
      );
      payload.ragione_sociale = form.ragione_sociale; // required non-null
      payload.country = form.country || "IT";
      if (editingId) await api.put(`/companies/${editingId}`, payload);
      else await api.post("/companies", payload);
      toast.success("Salvato");
      setForm(empty); setEditingId(null); setShowForm(false);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };

  const onEdit = (c) => {
    setForm({ ...empty, ...Object.fromEntries(Object.entries(c).filter(([k, v]) => k in empty && v != null)) });
    setEditingId(c.id); setShowForm(true);
  };

  const onDelete = async (id) => {
    if (!confirm("Eliminare l'azienda?")) return;
    try { await api.delete(`/companies/${id}`); toast.success("Eliminata"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };

  return (
    <div className="p-6 md:p-12 max-w-6xl">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
        <div>
          <p className="text-xs tracked text-blue-700 mb-2">CLIENTI BUSINESS</p>
          <h1 className="font-heading font-semibold text-4xl tracking-tight">Aziende</h1>
          <p className="text-slate-600 text-sm mt-2 max-w-xl">Aziende intestatarie della prenotazione. Utile quando un'impresa manda operai o trasfertisti: <strong>fattura unica all'azienda</strong>, una <strong>schedina Alloggiati per ogni occupante</strong>.</p>
        </div>
        <button onClick={() => { setShowForm(true); setEditingId(null); setForm(empty); }}
          data-testid="add-company"
          className="inline-flex items-center gap-2 bg-stone-900 text-stone-50 px-4 py-2.5 text-sm rounded-lg hover:bg-stone-800">
          <Plus className="w-4 h-4" /> Nuova azienda
        </button>
      </div>

      {showForm && (
        <form onSubmit={onSubmit} className="border border-slate-200 bg-white p-6 mb-8 grid md:grid-cols-3 gap-4" data-testid="company-form">
          <div className="md:col-span-3">
            <Field label="Ragione sociale *">
              <input required value={form.ragione_sociale} onChange={set("ragione_sociale")} className={inputCls} data-testid="company-name" />
            </Field>
          </div>
          <Field label="Partita IVA"><input value={form.partita_iva} onChange={set("partita_iva")} className={inputCls} data-testid="company-piva" placeholder="IT12345678901" /></Field>
          <Field label="Codice Fiscale"><input value={form.codice_fiscale} onChange={set("codice_fiscale")} className={inputCls} placeholder="Se diverso da P.IVA" /></Field>
          <Field label="Codice SDI (7 caratteri)"><input maxLength={7} value={form.sdi_code} onChange={(e) => setForm(f => ({ ...f, sdi_code: e.target.value.toUpperCase() }))} className={`${inputCls} uppercase`} placeholder="Per fatturazione elettronica" data-testid="company-sdi" /></Field>
          <Field label="PEC"><input type="email" value={form.pec} onChange={set("pec")} className={inputCls} placeholder="azienda@pec.it" /></Field>
          <Field label="Email"><input type="email" value={form.email} onChange={set("email")} className={inputCls} /></Field>
          <Field label="Telefono"><input value={form.phone} onChange={set("phone")} className={inputCls} /></Field>
          <div className="md:col-span-3 pt-2 border-t border-slate-200">
            <div className="text-xs tracked text-slate-500 mb-2">INDIRIZZO</div>
          </div>
          <div className="md:col-span-2">
            <Field label="Via e numero"><input value={form.address} onChange={set("address")} className={inputCls} /></Field>
          </div>
          <Field label="CAP"><input maxLength={5} value={form.cap} onChange={set("cap")} className={inputCls} /></Field>
          <Field label="Città"><input value={form.city} onChange={set("city")} className={inputCls} /></Field>
          <Field label="Paese"><input maxLength={2} value={form.country} onChange={(e) => setForm(f => ({ ...f, country: e.target.value.toUpperCase() }))} className={`${inputCls} uppercase`} /></Field>
          <div />
          <div className="md:col-span-3 flex gap-2">
            <button data-testid="company-submit" className="bg-stone-900 text-stone-50 px-5 py-2.5 text-sm rounded-lg hover:bg-stone-800">Salva</button>
            <button type="button" onClick={() => { setShowForm(false); setForm(empty); setEditingId(null); }} className="text-slate-600 px-4 py-2 text-sm">Annulla</button>
          </div>
        </form>
      )}

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {rows.length === 0 ? (
          <div className="col-span-full text-center text-slate-500 p-12 border border-dashed border-slate-300">
            Nessuna azienda. Aggiungine una per abilitare le prenotazioni business.
          </div>
        ) : rows.map(c => (
          <div key={c.id} className="border border-slate-200 p-6 bg-white" data-testid={`company-card-${c.id}`}>
            <Briefcase className="w-6 h-6 text-blue-700 mb-3" strokeWidth={1.5} />
            <h3 className="font-heading font-semibold text-2xl tracking-tight">{c.ragione_sociale}</h3>
            {c.partita_iva && <div className="text-xs text-slate-500 mt-2">P.IVA {c.partita_iva}</div>}
            {c.sdi_code && <div className="text-xs text-slate-500">SDI {c.sdi_code}</div>}
            {c.city && <div className="text-sm text-slate-600 mt-3">{c.city}{c.country !== "IT" ? ` · ${c.country}` : ""}</div>}
            <div className="mt-5 flex gap-2">
              <button onClick={() => onEdit(c)} className="px-3 py-2 text-sm text-slate-600 hover:text-blue-700"><Edit3 className="w-4 h-4" /></button>
              <button onClick={() => onDelete(c.id)} className="px-3 py-2 text-sm text-slate-600 hover:text-red-700"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const inputCls = "w-full border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-blue-700 bg-white";
function Field({ label, children }) {
  return (<label className="block"><span className="text-xs text-slate-500">{label}</span><div className="mt-1">{children}</div></label>);
}
